import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import axios from 'axios';
import { ArrowLeft, BookOpen, Calendar, ExternalLink, User, Loader, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL}/api`;

// Content Security Functions - Frontend Decoding (same as VolumePage)
const decodeContent = (encodedString) => {
  try {
    // Reverse the obfuscation
    const base64String = encodedString.split('').reverse().join('');
    
    // Use fetch to decode Base64 with proper UTF-8 handling
    const binaryString = atob(base64String);
    
    // Convert binary string to UTF-8 using escape/unescape (more compatible)
    const utf8String = decodeURIComponent(escape(binaryString));
    
    // Parse JSON
    const contentDict = JSON.parse(utf8String);
    return contentDict;
  } catch (error) {
    console.error('Error decoding content:', error);
    
    // Fallback: try direct atob (for backward compatibility)
    try {
      const base64String = encodedString.split('').reverse().join('');
      const jsonString = atob(base64String);
      const contentDict = JSON.parse(jsonString);
      return contentDict;
    } catch (fallbackError) {
      console.error('Fallback decoding also failed:', fallbackError);
      throw error;
    }
  }
};

const TemaDetail = () => {
  const [tema, setTema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { isAuthenticated, loading: authLoading } = useAuth();
  const { isDarkMode: darkMode } = useDarkMode();
  const navigate = useNavigate();
  const { temaId } = useParams();
  const [searchParams] = useSearchParams();
  
  // Get return URL from query params (where user came from)
  const returnUrl = searchParams.get('from') || '/temas';

  // Redirect if not authenticated (but wait for auth loading to complete)
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Fetch tema content
  useEffect(() => {
    const fetchTema = async () => {
      try {
        setLoading(true);
        
        // Get token from localStorage
        const token = localStorage.getItem('authToken');
        
        const response = await axios.get(`${API}/tema/${temaId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        // Decode the encoded content from the API (consistent with main/optional files)
        if (response.data.encoded_content) {
          const decodedContent = decodeContent(response.data.encoded_content);
          setTema({
            id: temaId,
            title: decodedContent.title, 
            created_at: new Date().toISOString(), // API doesn't return metadata anymore
            content: decodedContent
          });
        } else {
          // Fallback for any legacy format
          setTema(response.data);
        }
      } catch (err) {
        console.error('Error fetching tema:', err);
        if (err.response?.status === 404) {
          setError('Tema no encontrado');
        } else {
          setError('Error cargando el tema');
        }
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && !authLoading && temaId) {
      fetchTema();
    }
  }, [isAuthenticated, authLoading, temaId]);

  const renderMarkdown = (text) => {
    // Simple markdown rendering for bold text
    return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  };

  // Handle volume link click - pass return URL to volume page
  const handleVolumeLink = (link) => {
    // Extract URL from markdown link format
    const urlMatch = link.match(/\[.*?\]\((.*?)\)/);
    if (urlMatch) {
      const url = urlMatch[1];
      if (url && url.startsWith('http')) {
        // Extract volume number and chapter parameters from the link
        const volumeMatch = url.match(/volume[\/=](\d+)/i);
        const chapterMatch = url.match(/[?&]chapter=(\d+)/i);
        
        if (volumeMatch) {
          const volumeNumber = volumeMatch[1];
          const chapterNumber = chapterMatch ? chapterMatch[1] : null;
          
          // Pass current tema detail page as return URL
          const currentUrl = `/tema/${temaId}${window.location.search}`;
          
          // Construct internal URL with chapter parameter if available
          let internalUrl = `/volume/${volumeNumber}`;
          const params = new URLSearchParams();
          params.set('from', currentUrl);
          if (chapterNumber) {
            params.set('chapter', chapterNumber);
          }
          internalUrl += `?${params.toString()}`;
          
          window.open(internalUrl, '_blank');
        } else {
          window.open(url, '_blank');
        }
      }
    }
  };

  if (!authLoading && !isAuthenticated) {
    return null;
  }

  if (loading || authLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${
        darkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="text-center">
          <Loader className="animate-spin h-8 w-8 text-purple-600 mx-auto mb-4" />
          <p className={darkMode ? 'text-gray-300' : 'text-gray-500'}>
            Cargando tema...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${
        darkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className={`text-xl font-semibold mb-2 ${
            darkMode ? 'text-white' : 'text-gray-800'
          }`}>
            {error}
          </h2>
          <button
            onClick={() => navigate(returnUrl)}
            className="text-purple-600 hover:text-purple-700 flex items-center justify-center mx-auto transition-colors"
          >
            <ArrowLeft size={16} className="mr-2" />
            Volver a Temas
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode ? 'bg-gray-900' : 'bg-gray-50'
    }`}>
      {/* Header */}
      <div className={`shadow-sm border-b transition-colors duration-300 ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate(returnUrl)}
              className={`flex items-center transition-colors ${
                darkMode 
                  ? 'text-gray-300 hover:text-white' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <ArrowLeft size={20} className="mr-2" />
              {returnUrl.includes('/volume/') ? 'Volver al Volumen' : 'Volver a Temas'}
            </button>
            
            {/* Breadcrumb */}
            <nav className={`text-sm ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              <span>Temas</span>
              <span className="mx-2">›</span>
              <span className={darkMode ? 'text-gray-200' : 'text-gray-800'}>
                {tema?.content?.title}
              </span>
            </nav>
          </div>
          
          <div className="flex items-center">
            <BookOpen size={32} className="text-purple-600 mr-3" />
            <h1 className={`text-3xl font-bold uppercase tracking-wide ${
              darkMode ? 'text-white' : 'text-gray-800'
            }`}>
              {tema?.content?.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {tema?.content?.items && tema.content.items.length > 0 ? (
          <div className="space-y-8">
            {tema.content.items.map((item, index) => (
              <div key={index} className={`rounded-lg shadow-md border overflow-hidden transition-colors duration-300 ${
                darkMode 
                  ? 'bg-gray-800 border-gray-700' 
                  : 'bg-white border-gray-200'
              }`}>
                {/* Item Header */}
                <div className={`px-6 py-4 border-b transition-colors duration-300 ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-700' 
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <BookOpen size={16} className="text-purple-600 mr-2" />
                        <span className={`font-medium ${
                          darkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>
                          Volumen {item.info.volume} - Índice {item.info.index}
                        </span>
                      </div>
                      
                      <div className="flex items-center">
                        <Calendar size={16} className={`mr-2 ${
                          darkMode ? 'text-gray-400' : 'text-gray-500'
                        }`} />
                        <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                          {item.info.date}
                        </span>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => handleVolumeLink(item.info.link)}
                      className="flex items-center text-purple-600 hover:text-purple-700 transition-colors font-medium"
                    >
                      <span className="text-sm">Ver capítulo</span>
                      <ExternalLink size={16} className="ml-2" />
                    </button>
                  </div>
                </div>

                {/* Dialogue Summaries */}
                <div className="p-6">
                  {item.dialogue_summary && item.dialogue_summary.map((dialogue, dialogueIndex) => (
                    <div key={dialogueIndex} className={dialogueIndex > 0 ? `mt-6 pt-6 border-t ${
                      darkMode ? 'border-gray-700' : 'border-gray-200'
                    }` : ''}>
                      <div className="flex items-center mb-3">
                        <User size={16} className="text-blue-600 mr-2" />
                        <span className={`font-semibold ${
                          darkMode ? 'text-blue-400' : 'text-blue-700'
                        }`}>
                          {dialogue.speaker}
                        </span>
                      </div>
                      
                      <div
                        className={`leading-relaxed text-justify ${
                          darkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}
                        dangerouslySetInnerHTML={{ 
                          __html: renderMarkdown(dialogue.summary) 
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <BookOpen size={48} className={`mx-auto mb-4 ${
              darkMode ? 'text-gray-600' : 'text-gray-300'
            }`} />
            <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
              No hay contenido disponible para este tema.
            </p>
          </div>
        )}

        {/* Footer Info */}
        {tema && (
          <div className={`mt-12 text-center text-sm border-t pt-6 transition-colors duration-300 ${
            darkMode 
              ? 'text-gray-400 border-gray-700' 
              : 'text-gray-500 border-gray-200'
          }`}>
            <p>
              {tema.content?.items?.length || 0} referencias encontradas
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemaDetail;